import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import moment, { Moment } from "moment";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarIcon,
  CloseIconCircle,
  CrossIcon,
  Delete2Icon,
  ExclamationIcon,
  PlusIcon,
  RestaurantIcon,
} from "../assets/icons";
import { InputNumber, MainSpinner } from "../components";
import { DeleteLabelIcon } from "../components/IconLabel";
import MealTypeSelector from "../components/MealTypeSelector";
import TextSearch from "../components/TextSearch";
import { useMeals } from "../hooks";
import useConfirm, { Transition } from "../hooks/useConfirm";
import useMealTypes from "../hooks/useMealTypes";
import Api from "../openapi";
import { MealBriefResponse, MenuResponse, PlanBriefResponse } from "../openapi/client";
import { MealSelection, MenuSelectionValues, MonthlyCache, SelectedMealType } from "../openapi/interfaces";
import { setNotification } from "../store/notificationSlice";
import { checkEmpty, checkIf, debounce, defaultFormat, displayFormat } from "../utils";
import { DateCalendar, DayCalendarSkeleton } from "@mui/x-date-pickers";
import CustomDay from "../components/Calendar/day";
import { RootState } from "../store";
import useLockedDays from "../hooks/useLockedDays";
import Can from "../components/Can";

export interface ICloneSelection {
  name?: string;
  dateString?: string;
}

export const calculateDateFrame = (initial?: Moment) => {
  const nowDateFrame: Moment[] = [];
  for (let j = -3; j < 4; j++) {
    let time: Moment;
    if (j < 0) {
      const start = checkIf(initial) ? moment(initial) : moment();
      time = start.subtract(Math.abs(j), "days");
    } else {
      const start = checkIf(initial) ? moment(initial) : moment();
      time = start.add(j, "days");
    }
    nowDateFrame.push(time);
  }
  return nowDateFrame;
};

export default function MealPlanning() {
  const [selectedPlan, setSelectedPlan] = useState(" ");
  const [plans, setPlans] = useState<PlanBriefResponse[]>();

  const dispatch = useDispatch();
  const meals = useMeals();
  const mealTypes = useMealTypes();
  const { lockedStart } = useLockedDays();
  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently remove the menu item from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [selectedDate, setSelectedDate] = useState<string>(moment().format(defaultFormat));
  const [selectedDateMoment, setSelectedDateMoment] = useState<Moment | null>(moment());
  const [selectedMonth, setSelectedMonth] = useState<Moment>(moment().startOf("month"));
  const [monthlyView, setMonthlyView] = useState<MonthlyCache>({});
  const [currentDateFrame, setCurrentDateFrame] = useState<Moment[]>(calculateDateFrame());
  const [currentMonthExp, setCurrentMonthExp] = useState("");
  const [activeMode, setActiveMode] = useState("month");
  const [calendarLoading, setCalendarLoading] = useState(false);

  const [addNewMenuLoading, setAddNewMenuLoading] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState({ id: "", date: "" });
  const [selectedMeals, setSelectedMeals] = useState<MealSelection>({});
  const [mealTypeSelectorOpen, setMealTypeSelectorOpen] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);

  const planChange = (event: SelectChangeEvent) => setSelectedPlan(event.target.value);

  const setLoaderForMealType = (type: MenuSelectionValues, value: boolean) => {
    setSelectedMeals((state) => {
      let temp = { ...state };
      temp[type]!.loading = value;
      return temp;
    });
  };

  const mealSelection = async (
    event: MouseEvent,
    value: MealBriefResponse | null,
    entry: [string, SelectedMealType]
  ) => {
    if (checkIf(value)) {
      const type = entry[0] as MenuSelectionValues;
      const selectedMealType = entry[1];

      // check if the meal is selected already
      if (selectedMeals[type]?.list.includes(String(value?.id))) {
        return;
      }

      // send add meal request to BE
      try {
        setLoaderForMealType(type, true);
        const res = await Api.MenuItem.addMealToMenuItem(String(selectedMealType.id), { mealId: String(value?.id) });
      } catch (error: any) {
        dispatch(
          setNotification({
            show: true,
            severity: "error",
            message: error.response?.data?.message || error.message,
          })
        );
        setLoaderForMealType(type, false);
        return;
      }

      setLoaderForMealType(type, false);

      setSelectedMeals((state) => {
        let temp = { ...state };
        const category = temp[type];
        if (category) {
          if (!category.list.includes(String(value?.id))) {
            temp[type]?.list.push(String(value?.id));
          }
        } else {
          temp[type] = { list: [String(value?.id)] };
        }
        return temp;
      });
    }
  };

  const setLimit = async (value: number, type: MenuSelectionValues) => {
    setSelectedMeals((state) => {
      let temp = { ...state };
      temp[type]!.limit = value;
      return temp;
    });
    try {
    } catch (error) {}
  };
  const db = debounce(setLimit, 1000);

  const limitChange = (value: number, type: MenuSelectionValues) => {
    db(value, type);
  };

  const deleteMeal = async (entry: [string, SelectedMealType], id: string) => {
    const type = entry[0] as MenuSelectionValues;
    const selectedMealType = entry[1];

    // send delete meal request to BE
    try {
      setLoaderForMealType(type, true);
      const res = await Api.MenuItem.deleteMealFromMenuItem(String(selectedMealType.id), id);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
      setLoaderForMealType(type, false);
      return;
    }

    setLoaderForMealType(type, false);

    setSelectedMeals((state) => {
      let temp = { ...state };
      const category = temp[type];
      if (category) {
        if (Array.isArray(category.list)) {
          const index = category.list.indexOf(id);
          if (index > -1) {
            temp[type]!.list.splice(index, 1);
          }
        }
      }
      return temp;
    });
  };

  const deleteMealType = async (entry: [string, SelectedMealType]) => {
    const answer = await confirmDelete();
    if (!answer) return;

    const type = entry[0] as MenuSelectionValues;
    const selectedMealType = entry[1];

    // Backend operation to delete
    setLoaderForMealType(type, true);
    try {
      await Api.Menu.deleteMenuItemFromMenu(selectedMenu.id, String(selectedMealType.id));
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
      setLoaderForMealType(type, false);
      return;
    }

    setSelectedMeals((state) => {
      let temp = { ...state };
      temp[type]!.list = [];
      temp[type]!.show = false;
      temp[type]!.loading = false;
      return temp;
    });
  };

  const mapMenuItemsToState = (state: MealSelection, menuResponse: MenuResponse) => {
    let temp = { ...state };
    Object.entries(state).forEach((entry) => {
      const key = entry[0];
      const value = entry[1];
      const lookup = menuResponse.items?.find((item) => item.type === key);
      temp[key as MenuSelectionValues] = {
        ...temp[key as MenuSelectionValues],
        id: lookup?.id,
        list: (lookup?.options?.map((option) => option.meal?.id) as string[]) || [],
        show: Boolean(lookup),
        limit: lookup?.limit,
      };
    });
    return temp;
  };

  const resetSelectedMeals = () => {
    setSelectedMeals((state) => {
      let temp: MealSelection = {};
      mealTypes.forEach((mealType) => {
        const index = String(mealType.name);
        temp[index as MenuSelectionValues] = { list: [], show: false, description: mealType.description };
      });
      return temp;
    });
  };

  const handleOpenMealTypeSelector = () => {
    if (checkEmpty(selectedPlan)) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: "Please select plan",
        })
      );
      return;
    }
    setMealTypeSelectorOpen(true);
  };
  const handleCloseMealTypeSelector = () => setMealTypeSelectorOpen(false);

  const handleConfirmMealTypeSelector = async (selections: MealSelection) => {
    setMealTypeSelectorOpen(false);

    if (Object.keys(selections).length === 0) return;

    setAddNewMenuLoading(true);

    if (!checkEmpty(selectedMenu?.id)) {
      // menuItems will be added to the menu

      for (const selection of Object.keys(selections)) {
        try {
          const res = await Api.Menu.addMenuItemToMenu(selectedMenu.id, {
            type: selection as MenuSelectionValues,
            limit: selections[selection as MenuSelectionValues]?.limit,
          });
          setSelectedMeals((state) => {
            return {
              ...state,
              [selection as MenuSelectionValues]: {
                ...state[selection as MenuSelectionValues],
                id: res.data.id,
                limit: res.data.limit,
                show: true,
              },
            };
          });
        } catch (error) {}
      }
    } else {
      // there is no menu, we will create it
      try {
        const res = await Api.Plan.createMenu(selectedPlan, {
          date: selectedDate,
          menuItems: Object.keys(selections).map((selection) => ({
            type: selection as MenuSelectionValues,
            limit: selections[selection as MenuSelectionValues]?.limit,
          })),
        });
        setSelectedMeals((state) => {
          return mapMenuItemsToState(state, res.data);
        });
        setSelectedMenu({ id: String(res.data.id), date: String(res.data.date) });
      } catch (error) {}
    }
    setAddNewMenuLoading(false);
  };

  useEffect(() => {
    const getPlans = async () => {
      try {
        const res = await Api.Plan.listBriefPlans();
        setPlans(res.data);
      } catch (error) {}
    };
    getPlans();
  }, []);

  useEffect(() => {
    resetSelectedMeals();
    const getMenus = async () => {
      setGlobalLoading(true);
      try {
        const res = await Api.Plan.listMenus(selectedDate, selectedPlan);
        setSelectedMeals((state) => {
          return mapMenuItemsToState(state, res.data);
        });
        setSelectedMenu({ id: String(res.data.id), date: String(res.data.date) });
      } catch (error) {
        setSelectedMenu({ id: "", date: selectedDate });
      }
      setGlobalLoading(false);
    };
    !checkEmpty(selectedPlan) && checkIf(selectedDate) && getMenus();
  }, [selectedPlan, selectedDate]);

  useEffect(() => {
    if (mealTypes.length) {
      resetSelectedMeals();
    }
  }, [mealTypes]);

  // calendar items

  const handleNextDay = () => {
    setCurrentDateFrame((state) => {
      let temp = [...state];
      temp.push(moment(temp[temp.length - 1]).add(1, "days"));
      temp.shift();
      return temp;
    });
  };
  const handlePreviousDay = () => {
    setCurrentDateFrame((state) => {
      let temp = [...state];
      temp.unshift(moment(temp[0]).subtract(1, "days"));
      temp.pop();
      return temp;
    });
  };

  // console.log("monthly view", monthlyView);
  // console.log("selected plan", selectedPlan);

  const grabMonthly = async (selectedDate: string, emptyCache?: boolean, refresh?: boolean) => {
    const monthlyCache: MonthlyCache = {};

    if (checkEmpty(selectedPlan) || (!emptyCache && !refresh && monthlyView[selectedDate])) {
      return;
    }

    let targetStart = moment(selectedDate).startOf("month").add(-10, "days");
    let targetEnd = moment(selectedDate).endOf("month").add(10, "days");

    try {
      setCalendarLoading(true);
      const res = await Api.Plan.listMenusBetween(
        targetStart.format(defaultFormat),
        targetEnd.format(defaultFormat),
        selectedPlan
      );

      while (targetStart.format(defaultFormat) !== targetEnd.format(defaultFormat)) {
        const index = targetStart.format(defaultFormat);
        const foundDay = res.data.find((day) => day.date === index);
        monthlyCache[index] = foundDay || { items: [] };
        targetStart.add(1, "day");
      }
      if (emptyCache) {
        setMonthlyView(monthlyCache);
      } else {
        setMonthlyView((state) => ({ ...state, ...monthlyCache }));
      }
    } catch (error) {}
    setCalendarLoading(false);
  };

  const daySelectHandler = (value: Moment) => {
    setSelectedDate(value.format(defaultFormat));
    setSelectedDateMoment(value);
    setSelectedMonth(moment(value).startOf("month"));
  };

  const handleDateCalendarChange = (value: Moment | null) => {
    if (value) {
      setSelectedDate(value.format(defaultFormat));
      setSelectedDateMoment(value);
      setSelectedMonth(moment(value).startOf("month"));
      setActiveMode("week");
      setCurrentDateFrame(calculateDateFrame(value));
    }
  };

  const goToToday = () => {
    handleDateCalendarChange(moment());
  };

  const handleActiveMode = (mode: string) => {
    if (mode === "month") {
      grabMonthly(selectedMonth.format(defaultFormat), undefined, true);
    }
    setActiveMode(mode);
  };
  const handleMonthChange = (month: Moment) => {
    // grab calendar items
    // check if the middle of the month is grabbed
    setSelectedMonth(month);
    const reference = month.format(defaultFormat);
    grabMonthly(moment(reference).add(15, "days").format(defaultFormat));
  };

  useEffect(() => {
    let exp = "";
    let uniqueMonths = Array.from(new Set(currentDateFrame.map((item) => item.format("MMMM YYYY"))));
    if (uniqueMonths.length === 2) {
      exp = uniqueMonths.join(" - ");
    } else {
      exp = uniqueMonths.join("");
    }
    setCurrentMonthExp(exp);
  }, [currentDateFrame]);

  useEffect(() => {
    const selection = moment.utc(selectedDate).startOf("day");
    if (checkIf(lockedStart) && selection.isBefore(moment.utc(lockedStart))) {
      setReadOnlyMode(true);
    } else {
      setReadOnlyMode(false);
    }

    // grab monthly information for the monthly view
    // every time date changes
    // send a request for the wrapping month
    selectedDate && !checkEmpty(selectedPlan) && grabMonthly(selectedDate);
  }, [selectedDate, lockedStart]);

  useEffect(() => {
    setMonthlyView({});
    !checkEmpty(selectedPlan) && grabMonthly(selectedMonth?.format(defaultFormat), true);
  }, [selectedPlan]);

  // CLONE FUNCTIONALITY
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCloneWeek, setSelectedCloneWeek] = useState<string>(" ");
  const [cloneTargets, setCloneTargets] = useState<ICloneSelection[]>([]);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [targetToClone, setTargetToClone] = useState<string>();

  const handlePanelClose = (...args: any) => {
    setDialogOpen(false);
    setSelectedCloneWeek(" ");
  };

  const eligibilityControl = (day: Moment) => {
    let foundMeal = false;
    for (let i = 0; i < 7; i++) {
      const target = day.clone().add(i, "days");
      const compare = monthlyView[target.format(defaultFormat)];
      const find = Boolean(compare?.items?.length);
      foundMeal = foundMeal || find;
    }
    return foundMeal;
  };

  const cloneSelect = (day: Moment) => {
    const DateFrame: Moment[] = [];
    setTargetToClone(day.format(defaultFormat));
    setDialogOpen(true);

    // limit the selection to previous 16 weeks
    const currentWeek = day.clone().startOf("day");
    let dateIndex = 1;
    while (DateFrame.length !== 16 && dateIndex !== 150) {
      const target = currentWeek.clone().subtract(dateIndex, "weeks");
      if (eligibilityControl(target)) {
        DateFrame.push(target);
      }
      dateIndex++;
    }

    const eligibleWeeks = DateFrame.map((value) => {
      return {
        name: `Week of ${moment(value).format("MMMM D")}`,
        dateString: value.format(defaultFormat),
      };
    });
    setCloneTargets(eligibleWeeks);
  };

  const clone = async () => {
    setCloneLoading(true);
    try {
      const res = await Api.Menu.copyPlanMenu(selectedPlan, {
        fromDate: selectedCloneWeek,
        toDate: targetToClone,
        days: 7,
      });
      grabMonthly(selectedMonth.format(defaultFormat), undefined, true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setCloneLoading(false);
    setDialogOpen(false);
    setSelectedCloneWeek(" ");
  };

  return (
    <>
      <Box className="meal-planning">
        <Box className="plan-upper">
          <Box className="plan-upper-side">
            <Box className="view-wrapper">
              <Button onClick={() => handleActiveMode("week")} className={`${activeMode === "week" ? "active" : ""}`}>
                Week
              </Button>
              <Button onClick={() => handleActiveMode("month")} className={`${activeMode === "month" ? "active" : ""}`}>
                Month
              </Button>
            </Box>
            <Box sx={{ marginLeft: "24px" }}>
              <Button
                onClick={goToToday}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  "&:hover": { background: "#f9f9f9" },
                  background: "#fff",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "10px 12px",
                  "&, & svg": { color: "text.dark2" },
                }}
                endIcon={<RestaurantIcon />}
              >
                Today's Menu
              </Button>
            </Box>
          </Box>
          <Box className="plan-upper-side">
            <Box className="plan-selection-wrapper">
              <Box>
                <Typography component={"span"}>Plan Selection:</Typography>
              </Box>
              <Box sx={{ width: 250 }}>
                <FormControl fullWidth>
                  <Select
                    value={selectedPlan}
                    onChange={planChange}
                    className="plan-selector"
                    sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "white !important" } }}
                  >
                    <MenuItem selected value={" "}>
                      Choose
                    </MenuItem>
                    {plans?.map((planItem) => (
                      <MenuItem key={planItem.id} value={planItem.id}>
                        {planItem.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        </Box>
        {activeMode === "week" ? (
          <Box className="plan-inner">
            <Box className="plan-inner-current-month">
              <Typography component={"span"}>{currentMonthExp}</Typography>
            </Box>
            <Box className="calendar-wrapper">
              <Box onClick={handlePreviousDay} className="icon-container">
                <ArrowLeftIcon />
              </Box>

              <Box className="days-wrapper">
                {currentDateFrame.map((frameItem) => {
                  const formattedDate = frameItem.format(defaultFormat);
                  const active = formattedDate === selectedDate ? "active" : "";
                  return (
                    <Box className={`day ${active}`} key={formattedDate} onClick={() => daySelectHandler(frameItem)}>
                      <Typography component={"span"} className="day-of-month">
                        {frameItem.format("D")}
                      </Typography>
                      <Typography component={"span"} className="name-of-day">
                        {frameItem.format("dddd")}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box onClick={handleNextDay} className="icon-container">
                <ArrowRightIcon />
              </Box>
            </Box>
            <Box className="table-wrapper">
              {globalLoading ? (
                <MainSpinner />
              ) : (
                <>
                  {Object.entries(selectedMeals).map((entry) => {
                    const key = entry[0];
                    const selectedMeal = entry[1];
                    const remaining = Number(selectedMeal.limit) - selectedMeal.list.length;

                    return (
                      <Fragment key={key}>
                        {selectedMeal.show ? (
                          <Box className="meal-type-row">
                            <Box className="meal-type-name-wrap">
                              <Typography className="name">{selectedMeal.description}</Typography>
                              <Box className="limit-wrap">
                                <Can permission="MEAL_PLANNING_EDIT">
                                  <InputNumber
                                    disabled={true}
                                    default={selectedMeal.limit}
                                    onChange={(limit) => limitChange(limit, key as MenuSelectionValues)}
                                  />
                                </Can>
                              </Box>
                              {remaining > 0 ? (
                                <Typography
                                  sx={{
                                    background: "rgba(226, 64, 64, 0.08)",
                                    color: "text.style",
                                    borderRadius: "4px",
                                    fontSize: "10px",
                                    textAlign: "center",
                                    padding: "2px 4px",
                                    margin: "0 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                  }}
                                >
                                  <ExclamationIcon />
                                  Please add {`${remaining === 1 ? "1 meal" : `${remaining} meals`}`}
                                </Typography>
                              ) : null}
                            </Box>
                            <Box className={`meal-type-list-wrap ${Boolean(selectedMeal.loading) ? "disabled" : ""}`}>
                              {selectedMeal.list.map((item) => {
                                const lookup = meals.find((mealItem) => mealItem.id === item);
                                return (
                                  <Box key={lookup?.id} className="meal-item-container">
                                    <Typography className="meal-name" component={"span"}>
                                      {lookup?.name}
                                    </Typography>
                                    {!readOnlyMode && (
                                      <Can permission="MEAL_PLANNING_EDIT">
                                        <CloseIconCircle
                                          className="icon"
                                          onClick={() => deleteMeal(entry, String(lookup?.id))}
                                        />
                                      </Can>
                                    )}
                                  </Box>
                                );
                              })}
                              {!readOnlyMode && (
                                <Box width={200}>
                                  <Can permission="MEAL_PLANNING_EDIT">
                                    <TextSearch meals={meals} onChange={(...args) => mealSelection(...args, entry)} />
                                  </Can>
                                </Box>
                              )}

                              {Boolean(selectedMeal.loading) && <MainSpinner />}
                            </Box>
                            {!readOnlyMode && (
                              <Box className="meal-type-delete">
                                <Box className="pointer-layer">
                                  <Delete2Icon />
                                </Box>
                                <Can permission="MEAL_PLANNING_DELETE">
                                  <Box className="delete-layer" onClick={() => deleteMealType(entry)}>
                                    <Box sx={{ width: "32px", display: "flex", justifyContent: "center" }}>
                                      <Delete2Icon />
                                    </Box>
                                    <Typography component={"span"}>Delete Menu</Typography>
                                  </Box>
                                </Can>
                              </Box>
                            )}
                          </Box>
                        ) : null}
                      </Fragment>
                    );
                  })}
                  <ConfirmDialog />

                  <Box className="empty-meal-type">
                    {!checkEmpty(selectedPlan) ? (
                      <>
                        {!readOnlyMode ? (
                          <Can permission="MEAL_PLANNING_CREATE">
                            <LoadingButton
                              color="secondary"
                              variant="contained"
                              loading={addNewMenuLoading}
                              sx={{ display: "flex", flexDirection: "column", margin: "24px 0" }}
                              onClick={handleOpenMealTypeSelector}
                            >
                              <PlusIcon />
                              <Typography>Add New Menu</Typography>
                              <MealTypeSelector
                                open={mealTypeSelectorOpen}
                                list={selectedMeals}
                                onClose={handleCloseMealTypeSelector}
                                onConfirm={handleConfirmMealTypeSelector}
                              />
                            </LoadingButton>
                          </Can>
                        ) : (
                          !Object.values(selectedMeals).some((m) => m.show) && (
                            <Typography component={"span"} sx={{ margin: "24px 0" }}>
                              No menu selected
                            </Typography>
                          )
                        )}
                      </>
                    ) : (
                      <Typography component={"span"} sx={{ margin: "24px 0" }}>
                        Please select a plan from Plan Selection
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        ) : (
          <Box className="plan-inner">
            <DateCalendar
              value={selectedDateMoment}
              dayOfWeekFormatter={(_day: string, date: Moment) => moment(date).format("dddd")}
              showDaysOutsideCurrentMonth
              views={["day"]}
              onMonthChange={handleMonthChange}
              onChange={handleDateCalendarChange}
              sx={[
                {
                  "&": {
                    width: "100%",
                    maxHeight: "unset",
                    height: "auto",
                  },
                  "& .MuiPickersDay-root, & .MuiPickersDay-root:not(.Mui-selected)": {
                    borderRadius: 0,
                    borderRight: "1px solid #E5E7EA",
                    borderBottom: "1px solid #E5E7EA",
                    borderLeft: "none",
                    borderTop: "none",
                    margin: 0,
                    width: "100%",
                    height: "145px",
                    position: "relative",
                    paddingTop: "8px",
                  },
                  "& .MuiPickersDay-root:hover, & .MuiPickersDay-root:focus": {
                    backgroundColor: "unset",
                  },
                  "& .MuiDayCalendar-weekContainer": {
                    margin: 0,
                  },
                  "& .MuiDayCalendar-weekContainer > button:first-of-type": {
                    borderLeft: "1px solid #E5E7EA",
                  },
                  "& .MuiDayCalendar-monthContainer .MuiDayCalendar-weekContainer:first-of-type button": {
                    borderTop: "1px solid #E5E7EA",
                  },
                  "& .MuiDayCalendar-weekContainer:last-child button:first-of-type": {
                    borderBottomLeftRadius: "8px",
                  },
                  "& .MuiDayCalendar-weekContainer:last-child button:last-child": {
                    borderBottomRightRadius: "8px",
                  },
                  "& .MuiPickersSlideTransition-root": {
                    minHeight: "870px",
                  },
                  "& .MuiPickersDay-today": {
                    // background: "none",
                  },
                  "& .MuiPickersDay-dayOutsideMonth > svg, & .MuiPickersDay-dayOutsideMonth > span": {
                    opacity: "0.25",
                  },
                  "& .MuiPickersDay-root > svg": {},
                  "& .MuiPickersDay-root > span.MuiTypography-root": {},
                },
                (theme) => ({
                  "& .MuiPickersDay-root > span.MuiTypography-root": {
                    position: "absolute",
                    left: "5px",
                    top: "5px",
                    color: theme.palette.text.dark,
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: "normal",
                    borderRadius: "100%",
                    padding: "2px",
                    width: "23px",
                  },
                  "& .MuiPickersCalendarHeader-root": {
                    borderTopRightRadius: "8px",
                    borderTopLeftRadius: "8px",
                    borderBottomStyle: "solid",
                    borderBottomWidth: "1px",
                    borderBottomColor: theme.palette.text.light3,
                    margin: 0,
                    paddingTop: "24px",
                    paddingBottom: "20px",
                    justifyContent: "center",
                  },
                  "& .MuiPickersCalendarHeader-root .MuiPickersCalendarHeader-labelContainer ": {
                    marginRight: 0,
                    fontSize: "16px",
                    fontWeight: 600,
                    lineHeight: "normal",
                  },
                  "& .MuiDayCalendar-weekDayLabel": {
                    width: "100%",
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: 400,
                    color: theme.palette.text.fontLight3,
                    // backgroundColor: theme.palette.text.light3,
                  },
                  "& .MuiDayCalendar-header": {
                    //   backgroundColor: theme.palette.text.light3,
                  },
                  "& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root:focus.Mui-selected":
                    {
                      backgroundColor: "transparent",
                    },
                  "& .MuiPickersDay-root:not(.Mui-selected)": {},
                  "& .MuiPickersDay-root.Mui-selected svg, & .MuiPickersDay-root.Mui-selected span.MuiTypography-root":
                    {
                      // color: "white",
                    },
                }),
              ]}
              slots={{
                day: CustomDay,
              }}
              slotProps={{
                day: {
                  daysinfo: monthlyView,
                  selectedPlan: selectedPlan,
                  mealTypes: mealTypes,
                  loading: calendarLoading,
                  lockedStart: lockedStart,
                  onCloneSelect: cloneSelect,
                } as any,
              }}
            />
          </Box>
        )}
      </Box>
      <Fragment>
        <Dialog
          open={dialogOpen}
          TransitionComponent={Transition}
          onClose={handlePanelClose}
          aria-describedby="alert-dialog-slide-description"
          sx={{
            "& .MuiPaper-root": {
              borderRadius: "16px",
              boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
              padding: "24px",
              width: "564px",
              maxWidth: "564px",
            },
            "& .MuiDialogTitle-root": {
              padding: "0 16px 16px 0",
            },
            "& .MuiDialogContent-root,& .MuiDialogActions-root": {
              padding: 0,
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "text.dark",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
            }}
          >
            Copy From Another Period
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handlePanelClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "text.fontLight2",
            }}
          >
            <CrossIcon />
          </IconButton>
          <DialogContent>
            <Box sx={{ display: "flex", gap: "16px", flexDirection: "column", minHeight: "80px" }}>
              <Box sx={{ display: "flex", gap: "12px", alignItems: "baseline", flexDirection: "column" }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "text.base500" }}>
                  You are going to copy meal plan from a previous week to{" "}
                  <b>Week of {moment(targetToClone).format("MMMM D")}</b>
                </Typography>
                <Box className="form-section" sx={{ width: "250px" }}>
                  <Typography component="span" className="form-label">
                    Select Week to Clone from
                  </Typography>
                  <Select
                    value={selectedCloneWeek || null}
                    onChange={(event) => setSelectedCloneWeek(String(event.target.value))}
                    sx={{ marginBottom: 0 }}
                  >
                    <MenuItem selected value={" "}>
                      Choose
                    </MenuItem>
                    {cloneTargets?.map((week) => (
                      <MenuItem key={week.dateString} value={week.dateString}>
                        {week.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions>
            <LoadingButton
              fullWidth
              disabled={checkEmpty(selectedCloneWeek.trim())}
              loading={cloneLoading}
              color="secondary"
              variant="contained"
              sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
              onClick={clone}
            >
              Clone
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Fragment>
    </>
  );
}
